// Tryb — onboarding.
//
// Five steps: who you are, how we reach you, the code of conduct, how you get
// paid, and proof it's you. Each step's required answers are checked by the API
// before it will advance, so the progress in the rail is the real thing.

import { useState } from "react";
import { ONBOARDING } from "@shared/fields.js";
import { countReq, hasErrors } from "@shared/validation.js";
import { Badge, Button, Icon } from "../ds/index.js";
import Brand from "../components/Brand.jsx";
import { FieldCell } from "../components/TrybField.jsx";
import ConductCodePanel from "../components/ConductCode.jsx";
import { useStudio } from "../lib/studioContext.js";
import { useFieldViews } from "../lib/useFieldViews.js";
import { api } from "../lib/api.js";

const overline = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  letterSpacing: "var(--tracking-wider)",
  textTransform: "uppercase",
};

// What the rail says beside "Verify identity" once something has been sent.
// A turned-down document still counts as sent, but the step needs doing again —
// saying "sent" there contradicts the banner telling them to re-upload.
const KYC_RAIL_LABEL = { Verified: "done", Rejected: "redo", Pending: "sent" };

export default function Onboarding({ onEnterStudio }) {
  const studio = useStudio();
  const { vals, state, verifyStatus, setField, showToast, applyStudio, withFlush } = studio;
  const { fieldViews } = useFieldViews();

  // Errors stay hidden until someone tries to move on from a step.
  const [attempted, setAttempted] = useState({});

  const index = Math.min(state.onboardingStep ?? 0, ONBOARDING.length - 1);
  const current = ONBOARDING[index];
  const kycDone = verifyStatus !== "Unverified";

  const nonKycIds = ONBOARDING.filter((s) => !s.kyc).flatMap((s) => s.ids);
  const overall = countReq(nonKycIds, vals);
  const pct = overall.total ? Math.round(((overall.done + (kycDone ? 1 : 0)) / (overall.total + 1)) * 100) : 0;

  const stepDone = (s) => (s.kyc ? kycDone : (() => { const c = countReq(s.ids, vals); return c.total === c.done; })());

  const goToStep = (i) =>
    withFlush(async () => {
      try {
        applyStudio(await api.goToOnboardingStep(i));
      } catch (e) {
        showToast("Couldn’t switch step", e.message);
      }
    });

  const currentHasErrors = current.kyc ? !kycDone : hasErrors(current.ids, vals);
  const currentCount = current.kyc ? null : countReq(current.ids, vals);

  const next = () =>
    withFlush(async () => {
      if (current.kyc) {
        try {
          applyStudio(await api.completeOnboarding());
          // Skipping verification drops them on the verification page rather
          // than home: it's the one thing standing between them and listing.
          onEnterStudio?.(kycDone ? "home" : "kyc");
          showToast(
            kycDone ? `You’re in, ${String(vals["A1.02"] || "there").split(" ")[0]}` : "Welcome in. One thing left.",
            kycDone
              ? "Your studio is ready. Let’s put something on the calendar."
              : "Send us a document whenever you’re ready — you can’t publish a tryb until it clears.",
          );
        } catch (e) {
          showToast("Couldn’t finish up", e.message);
        }
        return;
      }

      try {
        const res = await api.advanceOnboarding(index);
        setAttempted((a) => ({ ...a, [current.id]: false }));
        applyStudio(res);
      } catch (e) {
        // 422 means the step still has gaps — surface them inline.
        setAttempted((a) => ({ ...a, [current.id]: true }));
        if (e.status !== 422) showToast("Couldn’t continue", e.message);
      }
    });

  const showErrors = !!attempted[current.id];
  const fields = current.kyc && verifyStatus === "Verified" ? [] : fieldViews(current.ids, showErrors);

  return (
    <div data-screen-label="Onboarding" style={{ display: "flex", width: "100%", height: "100vh", overflow: "hidden" }}>
      <aside
        className="tryb-rail"
        style={{
          width: 300,
          flex: "none",
          background: "var(--ink-800)",
          color: "var(--sand-75)",
          padding: "28px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 28,
          overflowY: "auto",
        }}
      >
        <Brand />

        <div>
          <div style={{ ...overline, opacity: 0.6, marginBottom: 10 }}>Set up your account</div>
          <div style={{ fontSize: "var(--text-sm)", opacity: 0.75, lineHeight: 1.55 }}>
            Five steps, and you’re ready to host your first tryb.
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {ONBOARDING.map((s, i) => {
            const done = stepDone(s);
            const on = i === index;
            const c = s.kyc ? null : countReq(s.ids, vals);
            return (
              <button
                key={s.id}
                onClick={() => goToStep(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  borderRadius: "var(--radius-md)",
                  fontFamily: "var(--font-body)",
                  color: "var(--sand-75)",
                  background: on ? "color-mix(in srgb, var(--sand-75) 12%, transparent)" : "transparent",
                  fontWeight: on ? 700 : 500,
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    flex: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    background: done ? "var(--sage-600)" : on ? "var(--ember-500)" : "color-mix(in srgb, var(--sand-75) 14%, transparent)",
                    color: "var(--sand-50)",
                  }}
                >
                  {done ? "✓" : i + 1}
                </span>
                <span style={{ flex: 1, fontSize: "var(--text-sm)" }}>{s.short}</span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.7 }}>
                  {s.kyc ? (kycDone ? KYC_RAIL_LABEL[verifyStatus] || "sent" : "") : `${c.done}/${c.total}`}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: "auto", fontSize: "var(--text-xs)", lineHeight: 1.5, opacity: 0.7 }}>
          Documents go straight to our ops team, encrypted. Members only ever see that you’re verified — never what you uploaded.
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "48px 56px 60px" }}>
          <main style={{ maxWidth: 760, display: "flex", flexDirection: "column", gap: 28 }}>
            <div>
              <div style={{ ...overline, color: "var(--ember-700)" }}>
                {index === 0 ? "Welcome to tryb" : `Step ${index + 1} of ${ONBOARDING.length}`}
              </div>
              <h1
                style={{
                  margin: "8px 0 10px",
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-3xl)",
                  fontWeight: 800,
                  letterSpacing: "var(--tracking-tight)",
                  lineHeight: 1.05,
                }}
              >
                {index === 0 ? "Let’s get you set up" : current.title}
              </h1>
              <p style={{ margin: 0, fontSize: "var(--text-md)", color: "var(--text-secondary)", lineHeight: 1.55, maxWidth: 600 }}>
                {index === 0 ? `Start with the basics. ${current.blurb}` : current.blurb}
              </p>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 20px" }}>
              {current.kyc && <KycPanels />}
              {current.id === "conduct" && (
                <ConductCodePanel accepted={vals["A4.06"] === true} onAccept={() => setField("A4.06", true)} />
              )}
              {fields.map((f) => (
                <FieldCell key={f.id} f={f} />
              ))}
            </div>
          </main>
        </div>

        <div
          style={{
            padding: "16px 56px",
            background: "var(--surface-page)",
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Button variant="ghost" onClick={() => goToStep(Math.max(0, index - 1))} disabled={index === 0}>
              Back
            </Button>
            <div style={{ width: 160, height: 6, borderRadius: 999, background: "var(--sand-300)", overflow: "hidden" }}>
              <div style={{ height: "100%", background: "var(--ember-500)", width: `${pct}%`, transition: "width var(--dur-slow) var(--ease-out)" }} />
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
              {overall.done + (kycDone ? 1 : 0)} of {overall.total + 1} done
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {showErrors && currentHasErrors && (
              <span style={{ fontSize: "var(--text-sm)", color: "var(--crimson-600)", fontWeight: 600 }}>
                {current.kyc
                  ? "Upload one document to continue"
                  : `${currentCount.total - currentCount.done} to fix on this step`}
              </span>
            )}
            <Button variant="primary" size="lg" onClick={next}>
              {index === ONBOARDING.length - 1 ? (kycDone ? "Go to your studio" : "Skip for now") : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The banner above the identity fields: verified, with our team, or turned
 * down. Identity is checked by hand, so there is nothing for the facilitator to
 * do here but upload and wait.
 */
export function KycPanels() {
  const { verifyStatus } = useStudio();

  if (verifyStatus === "Unverified") return null;

  const look = {
    Verified: {
      bg: "var(--sage-100)",
      border: "var(--sage-200)",
      dot: "var(--sage-600)",
      ink: "var(--sand-50)",
      icon: "Check",
      title: "You’re verified.",
      body: "Checked and cleared by our team. Members will see a verified badge next to your name.",
    },
    Pending: {
      bg: "var(--honey-100)",
      border: "var(--honey-300)",
      dot: "var(--honey-500)",
      ink: "var(--ink-900)",
      icon: "Clock",
      title: "Got it. A person is checking.",
      body: "Usually within a day. You can draft your tryb meanwhile — publishing unlocks when this clears.",
    },
    Rejected: {
      bg: "var(--crimson-100)",
      border: "var(--crimson-300)",
      dot: "var(--crimson-500)",
      ink: "var(--sand-50)",
      icon: "Clock",
      title: "We couldn’t read that document.",
      body: "Upload a clearer photo or try the other document type. If you’re stuck, get in touch and we’ll sort it out.",
    },
  }[verifyStatus];

  if (!look) return null;

  return (
    <div
      style={{
        gridColumn: "span 2",
        background: look.bg,
        border: `1px solid ${look.border}`,
        borderRadius: "var(--radius-lg)",
        padding: "20px 22px",
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <span
        style={{
          width: 40, height: 40, borderRadius: "50%", background: look.dot, color: look.ink,
          display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none",
        }}
      >
        <Icon name={look.icon} size={20} />
      </span>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "var(--text-md)" }}>{look.title}</div>
        <div style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", marginTop: 2 }}>{look.body}</div>
      </div>
    </div>
  );
}
